import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import {
  CardGiftcardOutlined,
  RefreshOutlined,
  AddComment,
} from '@mui/icons-material';
import { Modal, Box } from '@mui/material';
import axios from 'axios';

import GridItem from '@/components/Grid/GridItem';
import DashboardInfoCard from '@/views/Dashboard/DashboardInfoCard';
import CardHeader from '@/components/Card/CardHeader';
import CardBody from '@/components/Card/CardBody';
import { API_BASE_URL } from '../../config';
import { modalStyle } from '@/views/Clients/Claim';
import styles from '@/assets/jss/material-dashboard-react/views/dashboardStyle';

const useStyles = makeStyles(styles);

export default function TangoGiftCardBalance() {
  const { t } = useTranslation();
  const classes = useStyles();

  const [tangoBalance, setTangoBalance] = useState(null);
  const [tangoAccounts, setTangoAccounts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const getTangoBalance = async () => {
    const accounts = (await axios.get(API_BASE_URL + '/getTangoAccount')).data;
    setTangoAccounts(accounts);
    setTangoBalance(
      accounts
        ?.map((item) => item.account?.currentBalance ?? 0)
        .reduce((a, b) => a + b, 0),
    );
  };

  useEffect(() => {
    getTangoBalance();
  }, []);

  return (
    <GridItem xs={12} sm={6} md={3}>
      <DashboardInfoCard
        color='warning'
        icon={<CardGiftcardOutlined color='secondary' />}
        category={t('dashboard.facility.cards.giftCardBalance')}
        value={
          <small>
            $
            {parseFloat(tangoBalance ?? 0).toLocaleString('us', {
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
            })}
          </small>
        }
        footer={
          <>
            <div className={classes.stats}>
              <h5 style={{ cursor: 'pointer' }} onClick={getTangoBalance}>
                {t('dashboard.facility.actions.reload')}
                <RefreshOutlined />
              </h5>
            </div>
            <div className={classes.stats}>
              <h5
                style={{ cursor: 'pointer' }}
                onClick={() => setIsModalOpen(true)}>
                {t('dashboard.facility.actions.replenishBalance')}
                <AddComment />
              </h5>
            </div>
          </>
        }
      />
      <Modal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}>
        <Box
          sx={{ ...modalStyle, maxHeight: '80vh' }}
          className='makeStyles-card-96'
          noValidate
          autoComplete='off'>
          <CardHeader style={{ transform: 'translateY(-50%)' }} color='primary'>
            <h4>{t('dashboard.facility.modal.title')}</h4>
          </CardHeader>

          <CardBody style={{ marginTop: '-40px', overflowY: 'scroll' }}>
            {tangoAccounts.map((account, index) => {
              const facilitiesList = account.facilitiesConnected
                ? account.facilitiesConnected
                    .split(',')
                    .map((f) => f.trim())
                    .filter((f) => f)
                : [];

              return (
                <div
                  key={account.account?.accountNumber || index}
                  style={{
                    marginBottom: '30px',
                    paddingBottom: '20px',
                    borderBottom:
                      index < tangoAccounts.length - 1
                        ? '1px solid #e0e0e0'
                        : 'none',
                  }}>
                  <h5 style={{ marginBottom: '15px', color: '#333' }}>
                    {t('dashboard.facility.modal.accountName')}:{' '}
                    {account.accountName}
                  </h5>

                  <div style={{ marginBottom: '10px' }}>
                    <strong>{t('dashboard.facility.modal.balance')}:</strong> $
                    {parseFloat(
                      account.account?.currentBalance ?? 0,
                    ).toLocaleString('us', {
                      maximumFractionDigits: 2,
                      minimumFractionDigits: 2,
                    })}
                  </div>

                  <div style={{ marginBottom: '10px' }}>
                    <strong>
                      {t('dashboard.facility.modal.associatedFacilities')}:
                    </strong>
                    {facilitiesList.length > 0 ? (
                      <ul style={{ marginTop: '5px', paddingLeft: '20px' }}>
                        {facilitiesList.map((facility, idx) => (
                          <li key={idx}>{facility}</li>
                        ))}
                      </ul>
                    ) : (
                      <div
                        style={{
                          marginTop: '5px',
                          paddingLeft: '10px',
                          fontStyle: 'italic',
                        }}>
                        No facilities connected
                      </div>
                    )}
                  </div>

                  <div style={{ marginTop: '15px', fontSize: '14px' }}>
                    <strong>
                      {t('dashboard.facility.modal.replenishInstructions')}:
                    </strong>
                    <div style={{ marginTop: '5px', paddingLeft: '10px' }}>
                      {t('dashboard.facility.modal.memoInstructions')}
                      <br />
                      {t('dashboard.facility.modal.tangoAccountLabel')}{' '}
                      {account.account?.accountNumber}
                    </div>
                  </div>
                </div>
              );
            })}

            <div
              style={{
                marginTop: '30px',
                paddingTop: '20px',
                borderTop: '2px solid #333',
              }}>
              <h5 style={{ marginBottom: '10px', color: '#333' }}>
                {t('dashboard.facility.modal.wireTransferInformation')}
              </h5>
              <b>
                {t('dashboard.facility.modal.bankDetails')} <br />
                {t('dashboard.facility.modal.routingNumber')} <br />
                {t('dashboard.facility.modal.accountNumber')} <br />
                {t('dashboard.facility.modal.beneficiary')} <br />
                {t('dashboard.facility.modal.address')}
                <br />
                {t('dashboard.facility.modal.city')}
              </b>
            </div>
          </CardBody>
        </Box>
      </Modal>
    </GridItem>
  );
}
